import React from "react";
import { FLAG_LABELS } from "../../utils/flagLabels";
import UserIconPickerModal from "./UserIconPickerModal";
import UserColorPickerModal from "./UserColorPickerModal";
import UserCountryPickerModal from "./UserCountryPickerModal";
import UserVoicePickerModal from "./UserVoicePickerModal";

function UserProfileModal({
  user,
  setUser,
  closeUserProfile,
  saveUserProfile,
}) {
  const [username, setUsername] = React.useState(user.username || "");
  const [saveError, setSaveError] = React.useState("");
  // Capture the first-visit flag at mount time, before App.jsx clears it from state.
  const showWelcome = React.useRef(user?.isFirstVisit === true).current;
  const [roomName, setRoomName] = React.useState(
    typeof user.roomName === "string" ? user.roomName : (user.username
      ? (user.username.length <= 13
        ? `${user.username}'s Room`
        : user.username)
      : "")
  );

  // Track the initial username for default room name logic
  const initialUsername = React.useRef(user.username || "");

  // When username changes, update roomName if it matches the old default
  React.useEffect(() => {
    let oldDefault = "";
    if (initialUsername.current) {
      oldDefault = initialUsername.current.length <= 13
        ? `${initialUsername.current}'s Room`
        : initialUsername.current;
    }
    if (roomName === oldDefault) {
      if (username) {
        setRoomName(username.length <= 13 ? `${username}'s Room` : username);
      } else {
        setRoomName("");
      }
    }
    // Do not update roomName if user has customized it
    // Update the ref for next time
    initialUsername.current = username;
  }, [username, roomName]);
  const [color, setColor] = React.useState(user.color || "#e57373");
  const [icon, setIcon] = React.useState(user.icon || "😀");
  const [showIconModal, setShowIconModal] = React.useState(false);
  const [showColorModal, setShowColorModal] = React.useState(false);
  const [showCountryModal, setShowCountryModal] = React.useState(false);
  const [flag, setFlag] = React.useState(user.flag || "us");
  const [audioEnabled, setAudioEnabled] = React.useState(
    typeof user.audioEnabled === "boolean" ? user.audioEnabled : true
  );
  const [darkMode, setDarkMode] = React.useState(
    typeof user.darkMode === "boolean" ? user.darkMode : false
  );
  const [allowSpectators] = React.useState(
    typeof user.allowSpectators === "boolean" ? user.allowSpectators : true
  );
  // Text-to-speech voice preference stored on the user profile
  const [ttsVoice, setTtsVoice] = React.useState(user.ttsVoice || '');
  const [availableVoices, setAvailableVoices] = React.useState([]);
  const [showVoiceModal, setShowVoiceModal] = React.useState(false);

  // load speech synthesis voices
  React.useEffect(() => {
    if (typeof window === 'undefined' || typeof window.speechSynthesis === 'undefined') return;
    const load = () => {
      const v = window.speechSynthesis.getVoices() || [];
      setAvailableVoices(v);
    };
    load();
    const handler = () => load();
    if (window.speechSynthesis.addEventListener) window.speechSynthesis.addEventListener('voiceschanged', handler);
    else window.speechSynthesis.onvoiceschanged = handler;
    return () => {
      try { window.speechSynthesis.removeEventListener && window.speechSynthesis.removeEventListener('voiceschanged', handler); } catch (e) {}
    };
  }, []);

  const onSave = async (e) => {
    e.preventDefault();
    setSaveError("");
    // Create a new user object with updated values
    const updatedUser = {
      ...user,
      username: username.trim(),
      roomName,
      color,
      icon,
      flag,
      audioEnabled,
      darkMode,
      allowSpectators,
      ttsVoice,
      isFirstVisit: false, // clear the first-visit flag on any save
    };
    const result = saveUserProfile ? await saveUserProfile(updatedUser) : { ok: true, user: updatedUser };
    if (!result?.ok) {
      setSaveError(result?.error || "Unable to save your profile.");
      return;
    }

    setUser(result.user || updatedUser);
    if (closeUserProfile) closeUserProfile();
  };

  return (
    <div>
      <div className="mb-4 w-full max-w-md mx-auto bg-gray-100 dark:bg-gray-800 p-4 rounded shadow flex flex-col h-[60vh]">
        <form
          className="flex flex-col gap-3 flex-1 overflow-y-auto pr-8 pl-1"
          onSubmit={onSave}
        >
          {showWelcome && (
            <div className="bg-blue-50 dark:bg-blue-900 border border-blue-300 dark:border-blue-600 rounded-lg px-4 py-3 mb-1">
              <p className="font-semibold text-blue-800 dark:text-blue-200 text-sm">👋 Welcome! Choose your name</p>
              <p className="text-blue-700 dark:text-blue-300 text-xs mt-0.5">You've been given a random name. Change it to something you like before jumping in! You can change it at any time by clicking on your username in the top-right corner.</p>
            </div>
          )}
          <label className="block font-semibold"><a href="/login">Username</a></label>
          <input
            className="border p-2 rounded w-full mb-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            maxLength={20}
            required
          />
          {saveError && (
            <div className="-mt-2 mb-3 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/30 dark:text-red-200">
              {saveError}
            </div>
          )}
          <label className="block font-semibold">Default Room Name</label>
          <input
            className="border p-2 rounded w-full mb-4 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700"
            value={roomName}
            onChange={(e) => setRoomName(e.target.value)}
            maxLength={20}
            placeholder="Default room name when creating a room"
          />
          <label className="block font-semibold">Icon / Color / Country</label>
          <div className="flex items-center gap-4 mb-4">
            <button
              type="button"
              className="flex item-center gap-2 text-4xl p-2 rounded dark:bg-gray-800"
              onClick={() => setShowIconModal(true)}
              aria-label="Pick Icon"
            >
              {icon}
            </button>
            <button
              type="button"
              className="flex items-center gap-2 p-5 rounded-full border   dark:bg-gray-800"
              style={{ backgroundColor: color }}
              onClick={() => setShowColorModal(true)}
              aria-label="Pick Color"
            />
            <button
              type="button"
              className="flex items-center gap-2 p-2 rounded  dark:bg-gray-800"
              onClick={() => setShowCountryModal(true)}
            >
              <img
                src={`/flags/${flag}.png`}
                alt={FLAG_LABELS[flag] || flag.toUpperCase()}
                onError={(e) => { try { e.target.onerror = null; e.target.src = `/flags/${flag}.svg`; } catch (e) {} }}
                className="w-14 h-10 object-contain"
              />
              
            </button>
          </div>
          <UserIconPickerModal
            open={showIconModal}
            onClose={() => setShowIconModal(false)}
            onSelect={setIcon}
            selectedIcon={icon}
          />
          <UserColorPickerModal
            open={showColorModal}
            onClose={() => setShowColorModal(false)}
            onSelect={setColor}
            selectedColor={color}
          />

          <UserCountryPickerModal
            open={showCountryModal}
            onClose={() => setShowCountryModal(false)}
            onSelect={setFlag}
            selectedCode={flag}
          />
          <label className="block mb-2 font-semibold">Preferences</label>
          <div className="flex flex-col gap-2 mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={audioEnabled}
                onChange={(e) => setAudioEnabled(e.target.checked)}
              />
              <span>Enable Audio</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={darkMode}
                onChange={(e) => setDarkMode(e.target.checked)}
              />
              <span>Dark Mode</span>
            </label>
            
            
            {/* <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={allowSpectators}
                onChange={(e) => setAllowSpectators(e.target.checked)}
              />
              <span>Allow Spectators</span>
            </label> */}
          </div>
          {/*
          <label className="block mb-2 font-semibold">Voice</label>
          <input
            className="border p-2 rounded w-full mb-4 bg-blue-100 dark:bg-blue-800 text-gray-900 dark:text-gray-100 border-gray-300 dark:border-gray-700 cursor-pointer"
            value={(() => {
                const cur = availableVoices.find(v => (v.voiceURI === ttsVoice || v.name === ttsVoice));
                return cur ? `${cur.name}${cur.lang ? ` (${cur.lang})` : ''}` : '(default)';
              })()}
            onClick ={(e) => setShowVoiceModal(true)}
            readOnly
          />
          <UserVoicePickerModal
            open={showVoiceModal}
            onClose={() => setShowVoiceModal(false)}
            availableVoices={availableVoices}
            selectedVoice={ttsVoice}
            onSelect={(v) => { setTtsVoice(v); setShowVoiceModal(false); }}
          />
          */}
          <div className="flex justify-center gap-4 mt-4 pt-4 bg-gray-100 dark:bg-gray-800 sticky bottom-0 left-0 right-0 z-10 rounded-b">
            <button
              type="button"
              className="bg-gray-300 dark:bg-gray-700 dark:text-gray-100 px-4 py-2 rounded hover:bg-gray-400 dark:hover:bg-gray-600"
              onClick={closeUserProfile}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 text-white px-4 py-2 rounded"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default UserProfileModal;

// VoicePicker replaced by modal-based picker: UserVoicePickerModal
